/**
 * RootLayout - Unified Application Root Wrapper
 *
 * Eliminates 325+ lines of duplicate authentication, providers, and bundle mode handling
 * across 5+ apps (admin, chat, home, developer, store).
 *
 * Provides:
 * - Authentication flow (session check, loading, redirects)
 * - QueryClient provider (shared instance)
 * - Error boundaries
 * - Toast and notification providers
 * - Bundle mode detection (abstracted from app logic)
 * - DevConsole (auto-enabled in standalone mode)
 * - BottomNavigationBar (auto-enabled in standalone mode)
 *
 * Usage:
 * ```tsx
 * // apps/admin/src/App.tsx
 * import { RootLayout } from '@room/shared-ui';
 *
 * export function App() {
 *   return (
 *     <RootLayout appName="Admin">
 *       <YourAppContent />
 *     </RootLayout>
 *   );
 * }
 * ```
 */

import { type ReactNode, useMemo, useEffect, useRef, useCallback } from 'react';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getSessionToken } from '@room/frontend-sdk';
import { LoadingScreen } from '../../components/LoadingScreen';
import { AppErrorBoundary } from '../../components/AppErrorBoundary';
import { ToastProvider } from '../../components/UI/ToastProvider';
import { NotificationProvider } from '../../components/UI/NotificationContext';
import DevConsole from '../../components/DevConsole';
import { type TabItem } from '../../components/BottomNavigationBar';
import { LiquidBubbleNav, type BubbleNavItem } from '../../components/LiquidBubbleNav';
import { PortalContainerProvider } from '../../contexts/PortalContainerContext';
import {
  ViewportModeProvider,
  type ViewportMode,
} from '../../contexts/ViewportModeContext';
import { AppViewport } from './AppViewport';
import { getLoginRedirectUrl, shouldRedirectToLogin, clearLoginRedirect } from '../../utils/auth';
import { useSession, useSessionStore } from '../../stores/session';
import { isBundleMode } from '../../utils/bundleMode';
import { createLogger } from '../../utils/logger';
import { sharedQueryClient } from '../../utils/queryClient';
import { useDevConsoleAllowed } from '../../stores/devConsoleAccess';

const logger = createLogger('RootLayout');

// Mirrors LOGIN_REDIRECT_TTL_MS in utils/auth — how long the session-storage
// debounce suppresses a repeat redirect. Used as the fallback delay before we
// force a redirect when the debounce strands us on the "Redirecting" screen.
const LOGIN_REDIRECT_FALLBACK_MS = 8000;

/* ---- Default navigation icons (SF Symbols style) ---- */
const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 17l-5-5 5-5" />
  </svg>
);

const ChevronLeftFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.7 18.7a1 1 0 0 1-1.4 0l-5.3-5.3a2 2 0 0 1 0-2.8l5.3-5.3a1 1 0 0 1 1.4 1.4L10.4 12l5.3 5.3a1 1 0 0 1 0 1.4z" />
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.2L12 3l9 7.2V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10.2z" />
    <path d="M9 21V14h6v7" />
  </svg>
);

const HomeFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.6 2.4a1 1 0 0 0-1.2 0l-9 7.2A1 1 0 0 0 2 10.2V20a2 2 0 0 0 2 2h5v-8h6v8h5a2 2 0 0 0 2-2V10.2a1 1 0 0 0-.4-.6l-9-7.2z" />
  </svg>
);

const ArrowClockwiseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 4 21 10 15 10" />
    <path d="M21 10l-3.5-3.5A8 8 0 1 0 20 15" />
  </svg>
);

const ArrowClockwiseFilledIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 3a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 1 1 0-2h3.17l-2.46-2.46a7 7 0 1 0 2.46 6.96 1 1 0 1 1 1.96.4A9 9 0 1 1 18.12 5.88L21 3.83V3z" />
  </svg>
);

const DEFAULT_NAVIGATION_TABS: TabItem[] = [
  { id: 'back', label: 'Back', icon: <ChevronLeftIcon />, activeIcon: <ChevronLeftFilledIcon />, route: '#back' },
  { id: 'home', label: 'Home', icon: <HomeIcon />, activeIcon: <HomeFilledIcon />, route: '/' },
  { id: 'refresh', label: 'Refresh', icon: <ArrowClockwiseIcon />, activeIcon: <ArrowClockwiseFilledIcon />, route: '#refresh' },
];

function handleDefaultTabChange(tab: TabItem) {
  if (tab.id === 'home') window.location.href = '/';
  else if (tab.id === 'back') window.history.back();
  else if (tab.id === 'refresh') window.location.reload();
}

/** Convert TabItem[] to BubbleNavItem[], wiring onClick for special routes */
function tabsToBubbleItems(tabs: TabItem[], handler?: (tab: TabItem) => void): BubbleNavItem[] {
  return tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    activeIcon: tab.activeIcon,
    route: tab.route.startsWith('#') ? undefined : tab.route,
    badge: tab.badge,
    onClick: handler
      ? () => handler(tab)
      : tab.route.startsWith('#')
        ? () => handleDefaultTabChange(tab)
        : undefined,
  }));
}

/** Standard layout chrome rendered by both RootLayout and RootLayoutPublic */
function StandardLayoutChrome({
  showDevConsole,
  showNavigation,
  devConsoleOffset,
  navigationTabs,
  onTabChange,
}: {
  showDevConsole: boolean;
  showNavigation: boolean;
  devConsoleOffset?: number;
  navigationTabs?: TabItem[];
  onTabChange?: (tab: TabItem) => void;
}) {
  const resolvedOffset = devConsoleOffset ?? 12;
  const tabs = navigationTabs ?? DEFAULT_NAVIGATION_TABS;
  const tabHandler = onTabChange ?? undefined;
  const bubbleItems = tabsToBubbleItems(tabs, tabHandler);

  // Note: the viewport-mode toggle item and the --app-bottom-inset CSS var
  // are managed by LiquidBubbleNav itself — anywhere a FAB is rendered
  // inside a ViewportModeProvider, the toggle appears and the inset is
  // applied. No injection needed here.

  // Check admin config (globalEnabled + per-user overrides) before showing
  const devConsoleAllowed = useDevConsoleAllowed();

  return (
    <>
      {showDevConsole && devConsoleAllowed && (
        <DevConsole forceEnabled forceVisible floatingOffsetPx={resolvedOffset} />
      )}
      {showNavigation && (
        <LiquidBubbleNav items={bubbleItems} />
      )}
    </>
  );
}

export interface RootLayoutProps {
  /**
   * Application name for display in loading screens
   */
  appName: string;

  /**
   * Children to render after authentication
   */
  children: ReactNode;

  /**
   * Require authentication before rendering children
   * @default true
   */
  requireAuth?: boolean;

  /**
   * Custom loading message
   */
  loadingMessage?: string;

  /**
   * Custom redirect message
   */
  redirectMessage?: string;

  /**
   * Custom QueryClient instance (optional - uses shared by default)
   */
  queryClient?: QueryClient;

  /**
   * Show dev console floating button.
   * @default true in standalone, false in bundle mode
   */
  showDevConsole?: boolean;

  /**
   * Show bottom navigation bar (Back / Home / Refresh).
   * @default true in standalone, false in bundle mode
   */
  showNavigation?: boolean;

  /**
   * Pixel offset from the bottom for the DevConsole floating button.
   * Defaults to 72 when navigation is shown, 12 otherwise.
   */
  devConsoleOffset?: number;

  /**
   * Custom tabs for the bottom navigation bar.
   * Defaults to [Back, Home, Refresh].
   */
  navigationTabs?: TabItem[];

  /**
   * Custom tab change handler for the bottom navigation bar.
   */
  onTabChange?: (tab: TabItem) => void;

  /**
   * How the content area interacts with the FAB.
   * - 'padded' (default): the scroll container that actually scrolls
   *   (Container leaf / Stack / Shell content) reserves --fab-clearance at its
   *   bottom as a page-coloured footer gap, so the FAB never covers the last
   *   item at max scroll. When `aspectRatio` is set, the available area also
   *   shrinks by --fab-clearance and content is letterboxed above the FAB.
   * - 'full': content uses the entire viewport; the FAB overlays it.
   *
   * The user can override this at runtime via the toggle item added to the
   * FAB; the choice is persisted per `appName` in localStorage.
   */
  viewportMode?: ViewportMode;

  /**
   * Aspect ratio (width / height) for fixed-canvas apps such as games. When
   * set, the content is centered and letterboxed/pillarboxed to preserve
   * this ratio inside the available area.
   */
  aspectRatio?: number;

  /**
   * Additional providers to wrap around children
   */
  additionalProviders?: (children: ReactNode) => ReactNode;
}

/**
 * RootLayout component - unified authentication and provider wrapper
 */
export function RootLayout({
  appName,
  children,
  requireAuth = true,
  loadingMessage,
  redirectMessage,
  queryClient = sharedQueryClient,
  showDevConsole,
  showNavigation,
  devConsoleOffset,
  navigationTabs,
  onTabChange,
  viewportMode = 'padded',
  aspectRatio,
  additionalProviders,
}: RootLayoutProps) {
  const { isAuthenticated, isLoading, hasCheckedSession, error, refresh } = useSession();
  const returnTo = useMemo(() => window.location.pathname + window.location.search, []);
  const isRunningInBundle = isBundleMode();

  // Debug logging (suppressed in production via logger)
  logger.debug(`[${appName}] State:`, {
    requireAuth,
    isAuthenticated,
    isLoading,
    hasCheckedSession,
    isRunningInBundle,
    hasToken: Boolean(getSessionToken()),
    error: error?.message,
  });

  // Determine if dev console should be shown (default: shown unless in bundle mode)
  const shouldShowDevConsole = showDevConsole ?? !isRunningInBundle;
  const shouldShowNavigation = showNavigation ?? !isRunningInBundle;

  // Debug details for loading screen
  const debugDetails = (
    <ul style={{ margin: 0, paddingLeft: '16px' }}>
      <li>{`hasToken: ${Boolean(getSessionToken())}`}</li>
      <li>{`isLoading: ${isLoading}`}</li>
      <li>{`hasCheckedSession: ${hasCheckedSession}`}</li>
      <li>{`isAuthenticated: ${isAuthenticated}`}</li>
      {error ? <li>{`error: ${error.message}`}</li> : null}
    </ul>
  );

  // Force navigation to the login screen, bypassing the debounce in
  // shouldRedirectToLogin(). Used by the manual escape button, the BFCache
  // restore handler, and the stuck-screen fallback timer so the user is never
  // permanently stranded on "Redirecting".
  const goToLogin = useCallback(() => {
    if (isRunningInBundle) return;
    clearLoginRedirect();
    window.location.assign(getLoginRedirectUrl(returnTo));
  }, [isRunningInBundle, returnTo]);

  // Handle authentication redirect
  useEffect(() => {
    if (isRunningInBundle) return;
    if (!(requireAuth && hasCheckedSession && !isLoading && !isAuthenticated)) return;
    if (window.location.pathname.startsWith('/login')) return;

    // Normal path: debounced redirect guards against tight root <-> /login loops.
    if (shouldRedirectToLogin(window.location.pathname)) {
      window.location.replace(getLoginRedirectUrl(returnTo));
      return;
    }

    // The debounce suppressed the redirect, yet we are rendering the
    // "Redirecting" dead-end. Guarantee forward progress: once the debounce
    // window elapses, force the redirect if we are still unauthenticated.
    // Without this, a back-nav / refresh / BFCache restore within the debounce
    // window leaves the user stuck on "Redirecting" forever.
    const fallback = setTimeout(() => {
      if (!useSessionStore.getState().isAuthenticated) {
        logger.debug(`[${appName}] Redirect debounce stranded — forcing login redirect`);
        goToLogin();
      }
    }, LOGIN_REDIRECT_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [hasCheckedSession, isAuthenticated, isLoading, returnTo, requireAuth, isRunningInBundle, appName, goToLogin]);

  // BFCache restore (mobile "reopen after a while"): pages restored from the
  // back/forward cache do NOT re-run effects, so a stale "Redirecting" screen
  // would hang indefinitely. Re-attempt the redirect on restore if the user is
  // still unauthenticated.
  useEffect(() => {
    if (isRunningInBundle || !requireAuth) return;
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const state = useSessionStore.getState();
      if (
        !state.isAuthenticated &&
        !window.location.pathname.startsWith('/login')
      ) {
        logger.debug(`[${appName}] BFCache restore while unauthenticated — redirecting`);
        goToLogin();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [isRunningInBundle, requireAuth, appName, goToLogin]);

  // Bundle mode: auto-retry session check once after a short delay
  // Background auth on the home screen may still be completing
  const bundleRetried = useRef(false);
  useEffect(() => {
    if (
      isRunningInBundle &&
      requireAuth &&
      hasCheckedSession &&
      !isAuthenticated &&
      !isLoading &&
      !bundleRetried.current
    ) {
      bundleRetried.current = true;
      const timer = setTimeout(() => {
        logger.debug(`[${appName}] Bundle mode: retrying session check`);
        refresh();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isRunningInBundle, requireAuth, hasCheckedSession, isAuthenticated, isLoading, appName, refresh]);

  // Loading state - checking session
  if (requireAuth && (!hasCheckedSession || isLoading)) {
    logger.debug(`[${appName}] Rendering LoadingScreen (checking session)`);
    return (
      <LoadingScreen
        title={`Loading ${appName}`}
        message={loadingMessage || `Checking your session before opening ${appName.toLowerCase()}.`}
        details={debugDetails}
        showDevConsole={shouldShowDevConsole}
      />
    );
  }

  // Not authenticated
  if (requireAuth && !isAuthenticated) {
    // Bundle mode: can't redirect — show contextual UI instead of dead-end "Redirecting..."
    if (isRunningInBundle) {
      logger.debug(`[${appName}] Rendering LoadingScreen (bundle: not signed in)`);
      return (
        <LoadingScreen
          title="Sign In Required"
          message={redirectMessage || `Sign in from the home screen to use ${appName}.`}
          details={debugDetails}
          showDevConsole={shouldShowDevConsole}
          actions={
            <button
              type="button"
              onClick={() => refresh()}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: '0.5px solid var(--border-primary, rgba(255,255,255,0.08))',
                background: 'var(--bg-secondary, #1C1C1E)',
                color: 'var(--color-primary, #0A84FF)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
              }}
            >
              Retry
            </button>
          }
        />
      );
    }

    // Browser mode: redirect to login page
    logger.debug(`[${appName}] Rendering LoadingScreen (redirecting)`);
    return (
      <LoadingScreen
        title="Redirecting"
        message={redirectMessage || "Taking you to the login screen."}
        details={debugDetails}
        showDevConsole={shouldShowDevConsole}
        actions={
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* Always-present escape hatch — the auto-redirect can be suppressed
                by the debounce or skipped after a BFCache restore, so the user
                must always have a manual way off this screen. */}
            <button
              type="button"
              onClick={goToLogin}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: '0.5px solid var(--color-primary, #0A84FF)',
                background: 'var(--color-primary, #0A84FF)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
              }}
            >
              Continue to login
            </button>
            {error ? (
              <button
                type="button"
                onClick={() => refresh()}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '0.5px solid var(--border-primary, rgba(255,255,255,0.08))',
                  background: 'var(--bg-secondary, #1C1C1E)',
                  color: 'var(--color-primary, #0A84FF)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
                }}
              >
                Retry session check
              </button>
            ) : null}
          </div>
        }
      />
    );
  }

  // Authenticated - render app with all providers
  logger.debug(`[${appName}] Rendering app content (authenticated)`);
  let content = children;

  // Apply additional providers if provided
  if (additionalProviders) {
    content = additionalProviders(content);
  }

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <NotificationProvider>
            <ViewportModeProvider defaultMode={viewportMode} appId={appName}>
              <PortalContainerProvider>
                <AppViewport aspectRatio={aspectRatio}>{content}</AppViewport>
              </PortalContainerProvider>
              <StandardLayoutChrome
                showDevConsole={shouldShowDevConsole}
                showNavigation={shouldShowNavigation}
                devConsoleOffset={devConsoleOffset}
                navigationTabs={navigationTabs}
                onTabChange={onTabChange}
              />
            </ViewportModeProvider>
          </NotificationProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export interface RootLayoutPublicProps {
  children: ReactNode;
  queryClient?: QueryClient;
  additionalProviders?: (children: ReactNode) => ReactNode;

  /**
   * Show dev console floating button.
   * @default true in standalone, false in bundle mode
   */
  showDevConsole?: boolean;

  /**
   * Show bottom navigation bar (Back / Home / Refresh).
   * @default true in standalone, false in bundle mode
   */
  showNavigation?: boolean;

  /**
   * Pixel offset from the bottom for the DevConsole floating button.
   * Defaults to 72 when navigation is shown, 12 otherwise.
   */
  devConsoleOffset?: number;

  /**
   * Custom tabs for the bottom navigation bar.
   * Defaults to [Back, Home, Refresh].
   */
  navigationTabs?: TabItem[];

  /**
   * Custom tab change handler for the bottom navigation bar.
   */
  onTabChange?: (tab: TabItem) => void;

  /**
   * How the content area interacts with the FAB. Defaults to 'padded'.
   * See RootLayoutProps.viewportMode for details.
   */
  viewportMode?: ViewportMode;

  /**
   * Aspect ratio (width / height) for fixed-canvas apps. When set, content
   * is letterboxed/pillarboxed to preserve the ratio.
   */
  aspectRatio?: number;

  /**
   * Identifier used to scope per-app viewport-mode persistence. Optional —
   * when omitted, the mode is not persisted across sessions.
   */
  appId?: string;
}

/**
 * RootLayout without authentication requirement.
 * Useful for public pages or apps that handle auth differently.
 *
 * Includes DevConsole and BottomNavigationBar by default in standalone mode.
 * Both are hidden in bundle mode (the host app provides its own chrome).
 */
export function RootLayoutPublic({
  children,
  queryClient = sharedQueryClient,
  additionalProviders,
  showDevConsole,
  showNavigation,
  devConsoleOffset,
  navigationTabs,
  onTabChange,
  viewportMode = 'padded',
  aspectRatio,
  appId,
}: RootLayoutPublicProps) {
  const isRunningInBundle = isBundleMode();
  const shouldShowDevConsole = showDevConsole ?? !isRunningInBundle;
  const shouldShowNavigation = showNavigation ?? !isRunningInBundle;

  let content = children;

  if (additionalProviders) {
    content = additionalProviders(content);
  }

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <NotificationProvider>
            <ViewportModeProvider defaultMode={viewportMode} appId={appId}>
              <PortalContainerProvider>
                <AppViewport aspectRatio={aspectRatio}>{content}</AppViewport>
              </PortalContainerProvider>
              <StandardLayoutChrome
                showDevConsole={shouldShowDevConsole}
                showNavigation={shouldShowNavigation}
                devConsoleOffset={devConsoleOffset}
                navigationTabs={navigationTabs}
                onTabChange={onTabChange}
              />
            </ViewportModeProvider>
          </NotificationProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
